import NegotiationInvitationList from '@/components/Room/NegotiationInvitationList'
import InviteDropdown from '@/components/Student/InviteDropdown'
import RoundDropdown from '@/components/Student/RoundDropdown'
import StoryTimeline from '@/components/Student/StoryTimeline'
import TeamMembersPopover from '@/components/Student/TeamMembersPopover'
import { Button } from '@/components/ui/button'
import { ActiveTab, isAIPlayerTab, isCharterTab } from '@/constants/chat'
import { UserRole } from '@/constants/room'
import {
  terminateNegotiation,
  loadNegotiationMessages,
} from '@/redux/asyncThunks/negotiationWebsocket'
import { loadTeamChatMessages } from '@/redux/asyncThunks/teamChat'
import { loadProposals } from '@/redux/asyncThunks/teamDecision'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { setActiveTab, clearAdvisorUnread } from '@/redux/slices/chatSlice'
import { clearUnread } from '@/redux/slices/aiPlayerChatSlice'
import { clearTeamUnread } from '@/redux/slices/teamChatSlice'
import { clearNegotiationUnread } from '@/redux/slices/negotiationWebsocketSlice'
import { GameRoomStatus, Status } from '@/redux/types/room'
import { hasLeftGroupChat } from '@/utils/groupChatMembers'
import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  X,
  MessageSquare,
  Lightbulb,
  FileCheck,
  Check,
  Users,
  ChartColumn,
  ChevronLeft,
  ChevronRight,
  Bot,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import {
  Negotiation,
  NegotiationMember,
  NegotiationType,
} from '@/redux/types/negotiationWebsocket'
import { TimerMode } from '@/components/settings/timer-configuration/types'
import { useGamePhase } from '@/hooks/useGamePhase'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'
import { buildSidebarTabs } from '@/components/OGE/Tabbar/buildSidebarTabs'
import NegotiationTabAvatars from '@/components/OGE/Tabbar/NegotiationTabAvatars'
import { isValidTeamId } from '@/utils/teamUtils'
import {
  NEGOTIATION_GROUP_LABELS,
  NEGOTIATION_GROUP_ORDER,
  SidebarTab,
  TabKind,
} from '@/components/OGE/Tabbar/sidebarTabTypes'

type TabbarProps = {
  showInviteDropdown?: boolean
  showNegotiationList?: boolean
  isDecision: boolean
  isManualMode: boolean
  embedded?: boolean
}

const Tabbar: React.FC<TabbarProps> = ({
  showInviteDropdown = true,
  showNegotiationList = true,
  isDecision = false,
  isManualMode = false,
  embedded = false,
}) => {
  const dispatch = useAppDispatch()
  const activeTab = useAppSelector((state) => state.chat.activeTab)
  const advisorUnreadCount = useAppSelector(
    (state) => state.chat.advisorUnreadCount
  )
  const teamDummyAssignment = useAppSelector(
    (state) => state.aiPlayerChat.teamAssignment
  )
  const unreadAIPlayerCount = useAppSelector(
    (state) => state.aiPlayerChat.unreadCount
  )
  const teamRoomUnreadCount = useAppSelector(
    (state) => state.teamChat.unreadCount
  )
  const negotiationUnreadById = useAppSelector(
    (state) => state.negotiationWS.unreadByNegotiationId
  )
  const roomMembers = useAppSelector((state) => state.room.roomMembers)
  const teams = useAppSelector((state) => state.team.teams)
  const { user } = useEffectiveUser()
  const negotiations = useAppSelector(
    (state) => state.negotiationWS.negotiations
  )
  const advisors = useAppSelector((state) => state.advisor.advisors)
  const teamChatbots = useAppSelector((state) => state.teamChatbot.teamChatbots)
  const { currentRoom } = useAppSelector((state) => state.room)
  const charterSession = useAppSelector((state) => state.charter.session)
  const { waitingRoomStatus } = useAppSelector(
    (state) => state.waitingRoomWS
  )
  const [searchParams] = useSearchParams()
  const [isTerminating, setIsTerminating] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { roundId, roundStatus, isNegotiationPhase, isRandRPhase, isAdvisorPhase } =
    useGamePhase()
  const prevActiveNegotiationIdsRef = useRef<Set<string>>(new Set())
  const effectiveCollapsed = embedded ? false : isCollapsed

  const negotiationIdFromURL = useMemo(
    () =>
      user?.role === UserRole.Professor
        ? searchParams.get('negotiationId') || ''
        : undefined,
    [user?.role, searchParams]
  )
  const playerId = useMemo(
    () => searchParams.get('playerId') || undefined,
    [searchParams]
  )
  const teamIdFromURL = useMemo(
    () => searchParams.get('teamId') || undefined,
    [searchParams]
  )
  const isProfessorViewingPlayer = useMemo(
    () => user?.role === UserRole.Professor && !!playerId,
    [user?.role, playerId]
  )
  const isProfessorViewingTeam = useMemo(
    () =>
      user?.role === UserRole.Professor && !!teamIdFromURL && !playerId,
    [user?.role, teamIdFromURL, playerId]
  )

  const playerTeamId = useMemo(() => {
    if (isProfessorViewingTeam) {
      return teamIdFromURL ? parseInt(teamIdFromURL, 10) : null
    }
    if (!user?.email) return null
    const member = roomMembers.find((m) => m.member_email === user.email)
    return member?.teamId ?? null
  }, [isProfessorViewingTeam, teamIdFromURL, user?.email, roomMembers])

  const showTeamRoomTab = useMemo(() => {
    const isTeamMode = currentRoom?.game_mode === TimerMode.TEAM_MODE
    const isInProgress =
      currentRoom?.status === GameRoomStatus.IN_PROGRESS ||
      waitingRoomStatus === GameRoomStatus.IN_PROGRESS
    if (!isTeamMode || !isInProgress) return false
    return isValidTeamId(playerTeamId, teams)
  }, [
    currentRoom?.game_mode,
    currentRoom?.status,
    waitingRoomStatus,
    playerTeamId,
    teams,
  ])

  const activeNegotiations = useMemo(
    () =>
      negotiations.filter((negotiation) => {
        // A group chat begins as soon as YOU are in it - other invitees may
        // still be pending. Non-members (professor monitor) see it once any
        // member is in.
        if (negotiation.negotiation_type === NegotiationType.GROUP_ROOM) {
          const myRow = negotiation.members?.find(
            (member) => member.email === user?.email
          )
          if (myRow) {
            return (
              myRow.status === Status.Accepted ||
              myRow.status === Status.Owner
            )
          }
          return negotiation.members?.some(
            (member) =>
              member.status === Status.Accepted ||
              member.status === Status.Owner
          )
        }
        const allMembersAcceptedOrOwner = negotiation.members?.every(
          (member) =>
            member.status === Status.Accepted || member.status === Status.Owner
        )
        return allMembersAcceptedOrOwner
      }),
    [negotiations, user?.email]
  )

  const getCurrentNegotiationId = useCallback(() => {
    if (negotiationIdFromURL) {
      return negotiationIdFromURL
    }
    if (activeNegotiations.length > 0) {
      return activeTab
    }
    return null
  }, [negotiationIdFromURL, activeNegotiations.length, activeTab])

  const handleTabClick = useCallback(
    async (tabId: string) => {
      if (tabId === ActiveTab.Advisor) {
        dispatch(clearAdvisorUnread())
      } else if (isCharterTab(tabId)) {
        // Charter tabs only switch the active view; their data is driven by
        // the charter WS state managed in PlayerGame.
      } else if (tabId === ActiveTab.TeamRoom) {
        dispatch(clearTeamUnread())
        try {
          await dispatch(loadTeamChatMessages()).unwrap()
          await dispatch(loadProposals()).unwrap()
        } catch (error) {
          console.error('Failed to load team chat messages:', error)
        }
      } else if (isAIPlayerTab(tabId)) {
        dispatch(clearUnread())
      } else {
        const isNumericNegotiation =
          tabId !== ActiveTab.Decision &&
          tabId !== ActiveTab.Summary &&
          !isAIPlayerTab(tabId)

        if (isNumericNegotiation) {
          dispatch(clearNegotiationUnread(tabId))
          try {
            await dispatch(
              loadNegotiationMessages({
                negotiationId: tabId,
                page: 1,
                pageSize: 20,
              })
            ).unwrap()
          } catch (error) {
            console.error('Failed to load negotiation messages:', error)
          }
        }
      }

      dispatch(setActiveTab(tabId))
    },
    [dispatch]
  )

  const handleTerminate = useCallback(async () => {
    const negotiationId = getCurrentNegotiationId()
    if (!negotiationId) {
      console.warn('No active negotiation to terminate')
      return
    }

    setIsTerminating(true)
    try {
      await dispatch(terminateNegotiation({ negotiationId })).unwrap()
      dispatch(setActiveTab(null))
    } catch (error) {
      console.error('Failed to terminate negotiation:', error)
    } finally {
      setIsTerminating(false)
    }
  }, [dispatch, getCurrentNegotiationId])

  const getGroupChatLabel = useCallback(
    (negotiation: Negotiation, excludeEmail?: string) => {
      const others = (negotiation.members || []).filter(
        (member: NegotiationMember) =>
          member.email !== excludeEmail && !hasLeftGroupChat(member)
      )
      const names = others
        .slice(0, 2)
        .map(
          (member) =>
            member.name?.split(' ')[0] ||
            member.email?.split('@')[0] ||
            'Unknown'
        )
      const extra = others.length - names.length
      const label = names.join(', ')
      return extra > 0 ? `${label} +${extra}` : label || 'Group chat'
    },
    []
  )

  const getTabLabel = useCallback(
    (negotiation: Negotiation) => {
      if (negotiation.is_one_on_one) {
        const otherMember = negotiation.members?.find(
          (member: NegotiationMember) => member.email !== user?.email
        )
        return otherMember?.email?.split('@')[0] || 'Unknown'
      }
      if (negotiation.negotiation_type === NegotiationType.GROUP_ROOM) {
        return getGroupChatLabel(negotiation, user?.email)
      }
      const typeDisplay =
        negotiation.negotiation_type?.toLowerCase().replace(/_/g, ' ') ||
        'group'
      return `${typeDisplay}`
    },
    [user?.email, getGroupChatLabel]
  )

  const getProfessorTabLabel = useCallback(
    (negotiation: Negotiation) => {
      if (negotiation.is_one_on_one) {
        const memberNames = negotiation.members
          ?.map((member: NegotiationMember) => member.email?.split('@')[0])
          .join(' & ')
        return memberNames || 'Unknown'
      }
      if (negotiation.negotiation_type === NegotiationType.GROUP_ROOM) {
        return getGroupChatLabel(negotiation)
      }
      const typeDisplay =
        negotiation.negotiation_type?.toLowerCase().replace(/_/g, ' ') ||
        'group'
      return `${typeDisplay}`
    },
    [getGroupChatLabel]
  )

  useEffect(() => {
    if (user?.role !== UserRole.Professor) return

    if (isProfessorViewingPlayer) {
      dispatch(setActiveTab(ActiveTab.Advisor))
      return
    }

    if (isProfessorViewingTeam && teamIdFromURL) {
      dispatch(setActiveTab(ActiveTab.TeamRoom))
      dispatch(loadTeamChatMessages())
      dispatch(loadProposals({ gameRoundId: roundId || undefined }))
      return
    }

    if (negotiationIdFromURL) {
      const matchingNegotiation = activeNegotiations.find(
        (negotiation) =>
          String(negotiation.negotiationId) === negotiationIdFromURL
      )

      if (matchingNegotiation) {
        dispatch(setActiveTab(String(matchingNegotiation.negotiationId)))
        dispatch(
          loadNegotiationMessages({
            negotiationId: negotiationIdFromURL,
            page: 1,
            pageSize: 20,
          })
        )
      } else {
        dispatch(setActiveTab(negotiationIdFromURL))
      }
    }
  }, [
    user?.role,
    negotiationIdFromURL,
    teamIdFromURL,
    activeNegotiations,
    isProfessorViewingPlayer,
    isProfessorViewingTeam,
    roundId,
    dispatch,
  ])

  useEffect(() => {
    if (user?.role !== UserRole.Student) return

    if (!isNegotiationPhase || isDecision) {
      prevActiveNegotiationIdsRef.current = new Set(
        activeNegotiations.map((negotiation) => String(negotiation.negotiationId))
      )
      return
    }

    if (activeNegotiations.length === 0) {
      prevActiveNegotiationIdsRef.current = new Set()
      return
    }

    const currentIds = new Set(
      activeNegotiations.map((negotiation) => String(negotiation.negotiationId))
    )
    const previousIds = prevActiveNegotiationIdsRef.current
    const newlyActivated = activeNegotiations.find(
      (negotiation) => !previousIds.has(String(negotiation.negotiationId))
    )

    if (newlyActivated) {
      if (
        isNegotiationPhase &&
        teamDummyAssignment &&
        isAIPlayerTab(activeTab)
      ) {
        prevActiveNegotiationIdsRef.current = currentIds
        return
      }

      if (
        isNegotiationPhase &&
        !teamDummyAssignment &&
        activeTab !== ActiveTab.Advisor
      ) {
        prevActiveNegotiationIdsRef.current = currentIds
        return
      }

      if (
        isNegotiationPhase &&
        !teamDummyAssignment &&
        newlyActivated.negotiation_type !== NegotiationType.PUBLIC_ROOM
      ) {
        prevActiveNegotiationIdsRef.current = currentIds
        return
      }

      const negotiationId = String(newlyActivated.negotiationId)
      dispatch(setActiveTab(negotiationId))
      dispatch(
        loadNegotiationMessages({
          negotiationId,
          page: 1,
          pageSize: 20,
        })
      )
    }

    prevActiveNegotiationIdsRef.current = currentIds
  }, [
    activeNegotiations,
    dispatch,
    user?.role,
    isNegotiationPhase,
    isDecision,
    teamDummyAssignment,
    activeTab,
  ])

  const isCurrentNegotiationTerminated = useCallback(() => {
    const currentNegotiationId = getCurrentNegotiationId()
    if (!currentNegotiationId) return false

    const currentNegotiation = negotiations.find(
      (negotiation) => negotiation.negotiationId == currentNegotiationId
    )

    return !!currentNegotiation?.end_time
  }, [getCurrentNegotiationId, negotiations])

  const showDecisionTab = useMemo(
    () => !isManualMode && isDecision,
    [isManualMode, isDecision]
  )

  const showSummaryTab = useMemo(
    () =>
      user?.role !== UserRole.Professor &&
      roundStatus === ActivityState.Completed,
    [user?.role, roundStatus]
  )

  const showCharterPrepTab = useMemo(
    () =>
      user?.role !== UserRole.Professor &&
      currentRoom?.charter_enabled === true &&
      !!charterSession &&
      (charterSession.round_status === 'in_progress' ||
        charterSession.round_status === 'completed'),
    [
      user?.role,
      currentRoom?.charter_enabled,
      charterSession,
      charterSession?.round_status,
    ]
  )

  const showCharterGroupTab = useMemo(
    () =>
      user?.role !== UserRole.Professor &&
      currentRoom?.charter_enabled === true &&
      (charterSession?.phase === 'phase2_group' ||
        charterSession?.phase === 'ratified' ||
        charterSession?.status === 'locked'),
    [
      user?.role,
      currentRoom?.charter_enabled,
      charterSession?.phase,
      charterSession?.status,
    ]
  )

  useEffect(() => {
    if (
      user?.role === UserRole.Student &&
      !showSummaryTab &&
      activeTab === ActiveTab.Summary
    ) {
      dispatch(setActiveTab(ActiveTab.Advisor))
    }
  }, [user?.role, showSummaryTab, activeTab, dispatch])

  useEffect(() => {
    if (
      user?.role === UserRole.Student &&
      !isDecision &&
      activeTab === ActiveTab.Decision
    ) {
      dispatch(setActiveTab(ActiveTab.Advisor))
    }
  }, [user?.role, isDecision, activeTab, dispatch])

  useEffect(() => {
    if (isProfessorViewingTeam) return
    if (!showTeamRoomTab && activeTab === ActiveTab.TeamRoom) {
      dispatch(setActiveTab(ActiveTab.Advisor))
    }
  }, [showTeamRoomTab, activeTab, dispatch, isProfessorViewingTeam])

  useEffect(() => {
    if (
      user?.role === UserRole.Student &&
      !showCharterPrepTab &&
      activeTab === ActiveTab.CharterPrep
    ) {
      dispatch(setActiveTab(ActiveTab.Advisor))
    }
  }, [user?.role, showCharterPrepTab, activeTab, dispatch])

  // When the Group Moderator tab is no longer valid, redirect to the right tab:
  // - Round still in_progress = professor moved phase back to Phase 1 → CharterPrep
  // - Round ended / reset → Advisor
  useEffect(() => {
    if (!showCharterGroupTab && activeTab === ActiveTab.CharterGroup) {
      if (charterSession?.round_status === 'in_progress') {
        dispatch(setActiveTab(ActiveTab.CharterPrep))
      } else {
        dispatch(setActiveTab(ActiveTab.Advisor))
      }
    }
  }, [showCharterGroupTab, activeTab, charterSession?.round_status, dispatch])

  useEffect(() => {
    if (
      user?.role === UserRole.Student &&
      isDecision &&
      !isManualMode &&
      isAIPlayerTab(activeTab)
    ) {
      dispatch(setActiveTab(ActiveTab.Decision))
    }
  }, [user?.role, isDecision, isManualMode, activeTab, dispatch])

  const getTabIcon = useCallback((kind: TabKind) => {
    if (kind === 'advisor') return <Lightbulb className='w-3.5 h-3.5' />
    if (kind === 'charterPrep') return <Sparkles className='w-3.5 h-3.5' />
    if (kind === 'charterGroup') return <Users className='w-3.5 h-3.5' />
    if (kind === 'decision') return <FileCheck className='w-3.5 h-3.5' />
    if (kind === 'teamRoom') return <Users className='w-3.5 h-3.5' />
    if (kind === 'group') return <Users className='w-3.5 h-3.5' />
    if (kind === 'aiPlayer') return <Bot className='w-3.5 h-3.5' />
    if (kind === 'summary') return <ChartColumn className='w-3.5 h-3.5' />
    return <MessageSquare className='w-3.5 h-3.5' />
  }, [])

  const sidebarTabs = useMemo(
    () =>
      buildSidebarTabs({
        userRole: user?.role,
        userEmail: user?.email,
        showTeamRoomTab,
        isNegotiationPhase,
        teamDummyAssignment,
        activeNegotiations,
        showDecisionTab,
        showSummaryTab,
        showCharterPrepTab,
        showCharterGroupTab,
        advisorUnreadCount,
        aiPlayerUnreadCount: unreadAIPlayerCount,
        teamRoomUnreadCount,
        negotiationUnreadById,
        advisors,
        teamChatbots,
        isRandRPhase,
        isAdvisorPhase,
        roomMembers,
        playerTeamId,
        isProfessorViewingPlayer,
        isProfessorViewingTeam,
        negotiationIdFromURL,
        roomNegotiations: currentRoom?.negotiations ?? [],
        getTabLabel,
        getProfessorTabLabel,
      }),
    [
      user?.role,
      user?.email,
      showTeamRoomTab,
      isNegotiationPhase,
      teamDummyAssignment,
      activeNegotiations,
      showDecisionTab,
      showSummaryTab,
      showCharterPrepTab,
      showCharterGroupTab,
      advisorUnreadCount,
      unreadAIPlayerCount,
      teamRoomUnreadCount,
      negotiationUnreadById,
      advisors,
      teamChatbots,
      isRandRPhase,
      isAdvisorPhase,
      roomMembers,
      playerTeamId,
      isProfessorViewingPlayer,
      isProfessorViewingTeam,
      negotiationIdFromURL,
      currentRoom?.negotiations,
      getTabLabel,
      getProfessorTabLabel,
    ]
  )

  const channelTabs = useMemo(
    () => sidebarTabs.filter((tab) => tab.section === 'channels'),
    [sidebarTabs]
  )

  const negotiationTabs = useMemo(
    () => sidebarTabs.filter((tab) => tab.section === 'negotiations'),
    [sidebarTabs]
  )

  const groupedNegotiationTabs = useMemo(() => {
    const groups: Partial<Record<TabKind, SidebarTab[]>> = {}
    for (const tab of negotiationTabs) {
      if (!groups[tab.kind]) {
        groups[tab.kind] = []
      }
      groups[tab.kind]!.push(tab)
    }
    return NEGOTIATION_GROUP_ORDER.filter((kind) => groups[kind]?.length).map(
      (kind) => ({
        kind,
        label: NEGOTIATION_GROUP_LABELS[kind],
        tabs: groups[kind]!,
      })
    )
  }, [negotiationTabs])

  const showTerminateButton = useMemo(
    () =>
      user?.role === UserRole.Professor &&
      (getCurrentNegotiationId() !== null || isProfessorViewingPlayer) &&
      activeTab !== ActiveTab.Advisor &&
      activeTab !== ActiveTab.TeamRoom &&
      !isAIPlayerTab(activeTab),
    [
      user?.role,
      getCurrentNegotiationId,
      isProfessorViewingPlayer,
      activeTab,
    ]
  )

  const renderUnreadBadge = (count?: number) => {
    if (!count || count <= 0 || effectiveCollapsed) return null
    return (
      <span className='min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center'>
        {count > 99 ? '99+' : count}
      </span>
    )
  }

  const renderTabButton = useCallback(
    (tab: SidebarTab) => {
      const isActive = activeTab == tab.id
      const showAvatars =
        tab.kind !== 'decision' && tab.kind !== 'summary'

      return (
        <button
          key={tab.id}
          onClick={() => handleTabClick(tab.id)}
          className={cn(
            'w-full flex items-center rounded-md transition-all duration-200',
            effectiveCollapsed
              ? 'h-9 justify-center px-2'
              : 'h-9 justify-between px-3',
            isActive
              ? 'bg-[#E6ECF2] text-slate-900 hover:bg-[#DDE6F0] border-r-2 border-[#B9C8D8]'
              : 'text-slate-600 hover:bg-slate-100'
          )}
          title={effectiveCollapsed ? tab.label : undefined}
          aria-label={tab.label}
        >
          <div className='flex items-center gap-2 min-w-0 flex-1'>
            <div
              className={cn(
                'flex-shrink-0',
                isActive ? 'text-slate-900' : 'text-slate-500'
              )}
            >
              {getTabIcon(tab.kind)}
            </div>
            {!effectiveCollapsed && (
              <span className='truncate text-sm font-semibold capitalize'>
                {tab.label}
              </span>
            )}
          </div>
          {!effectiveCollapsed && (
            <div className='flex items-center gap-1.5 flex-shrink-0'>
              {renderUnreadBadge(tab.unreadCount)}
              {showAvatars && tab.avatars && (
                <NegotiationTabAvatars avatars={tab.avatars} maxVisible={5} />
              )}
            </div>
          )}
        </button>
      )
    },
    [activeTab, effectiveCollapsed, getTabIcon, handleTabClick]
  )

  const renderNegotiationsSection = () => (
    <section>
      {!effectiveCollapsed && (
        <p className='mb-2 px-1 text-sm font-semibold text-gray-700'>
          Negotiations
        </p>
      )}
      {negotiationTabs.length === 0 ? (
        !effectiveCollapsed && (
          <p className='px-2 py-2 text-[11px] text-slate-400'>
            No active negotiations
          </p>
        )
      ) : groupedNegotiationTabs.length > 1 ? (
        <div className='space-y-3'>
          {groupedNegotiationTabs.map((group) => (
            <div key={group.kind}>
              {!effectiveCollapsed && (
                <p className='mb-1 px-2 text-xs font-semibold text-gray-600'>
                  {group.label}
                </p>
              )}
              <div className='space-y-1'>
                {group.tabs.map(renderTabButton)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='space-y-1'>{negotiationTabs.map(renderTabButton)}</div>
      )}
    </section>
  )

  return (
    <div
      className={cn(
        'h-full flex flex-col overflow-hidden',
        embedded
          ? 'bg-transparent'
          : 'bg-slate-50 border-r border-slate-200 transition-all duration-200',
        !embedded && isCollapsed ? 'w-14' : '',
        !embedded && !isCollapsed ? 'w-64' : ''
      )}
    >
      {!embedded && (
        <div className='h-12 border-b border-slate-200 px-2 flex items-center justify-between'>
          {!effectiveCollapsed && (
            <span className='text-xl font-bold text-dark-blue'>Chat</span>
          )}
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8 text-slate-500 hover:text-slate-700'
            onClick={() => setIsCollapsed((prev) => !prev)}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className='w-4 h-4' />
            ) : (
              <ChevronLeft className='w-4 h-4' />
            )}
          </Button>
        </div>
      )}

      {user?.role !== UserRole.Professor && !effectiveCollapsed && (
        <div className='px-2 pt-[3.2rem] pb-2 border-b border-slate-200 flex items-center gap-2'>
          <StoryTimeline />
          <RoundDropdown />
        </div>
      )}

      <div className='flex-1 min-h-0 overflow-y-auto px-2 py-3 space-y-4'>
        <section>
          {!effectiveCollapsed && (
            <p className='mb-2 px-1 text-sm font-semibold text-gray-700'>
              Channels
            </p>
          )}
          <div className='space-y-1'>{channelTabs.map(renderTabButton)}</div>
        </section>

        {renderNegotiationsSection()}
      </div>

      <div className='border-t border-slate-200 px-2 py-2 space-y-2'>
        {user?.role !== UserRole.Professor &&
          showTeamRoomTab &&
          activeTab === ActiveTab.TeamRoom && (
            <div className={cn(effectiveCollapsed && 'flex justify-center')}>
              <TeamMembersPopover
                isTeamRoomActive={activeTab === ActiveTab.TeamRoom}
              />
            </div>
          )}
        {user?.role !== UserRole.Professor && showNegotiationList && (
          <div className={cn(effectiveCollapsed && 'flex justify-center')}>
            <NegotiationInvitationList />
          </div>
        )}

        {user?.role !== UserRole.Professor && showInviteDropdown && (
          <div className={cn(effectiveCollapsed && 'flex justify-center')}>
            <InviteDropdown />
          </div>
        )}

        {showTerminateButton && (
          <Button
            variant='destructive'
            size='sm'
            className={cn(
              'h-8 text-xs font-medium rounded-md shadow-sm hover:shadow transition-all duration-200 disabled:opacity-50',
              effectiveCollapsed ? 'w-full px-0' : 'w-full'
            )}
            onClick={handleTerminate}
            disabled={isTerminating || isCurrentNegotiationTerminated()}
            aria-label='Terminate session'
          >
            {isTerminating ? (
              <>
                <div className='w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5'></div>
                {!effectiveCollapsed && <span>Terminating...</span>}
              </>
            ) : isCurrentNegotiationTerminated() ? (
              <>
                <Check className='w-3 h-3 mr-1.5' />
                {!effectiveCollapsed && <span>Terminated</span>}
              </>
            ) : (
              <>
                <X className='w-3 h-3 mr-1.5' />
                {!effectiveCollapsed && <span>Terminate</span>}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

export default Tabbar
